import React from "react";
import { MessageSubComponentProps } from "./message-types/types";
import { cn } from "@/lib/utils";
import { MediaTypes } from "../components/multi-media";

export interface MessageContainerProps {
  left?: boolean;
  opaque?: boolean;
}

export const MessageContainer = React.forwardRef<
  HTMLDivElement,
  MessageContainerProps & React.HTMLAttributes<HTMLDivElement>
>(({ left, opaque, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center",
      !left && "justify-end",
      opaque && "opacity-50",
      className
    )}
    {...props}
  />
));
MessageContainer.displayName = "MessageContainer";

interface MultiMediaBackgroundProps {
  left?: boolean;
  marginBottom?: boolean;
  isReady?: boolean;
  mediaType?: string;
  portraitMedia?: boolean;
}

export const MultiMediaBackground = React.forwardRef<
  HTMLDivElement,
  MultiMediaBackgroundProps & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      left,
      marginBottom,
      isReady,
      mediaType,
      portraitMedia,
      className,
      style,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        "rounded-lg w-[150px] min-[1920px]:w-[350px] min-[2100px]:w-[500px] relative overflow-hidden",
        mediaType === MediaTypes.storyMention
          ? "bg-inbox-surface-sunken"
          : "bg-inbox-surface-raised",
        left ? "mr-4" : "ml-4",
        marginBottom && "mb-3",
        portraitMedia ? "min-h-[250px]" : "min-h-[100px]",
        isReady && "!min-h-0",
        className
      )}
      {...props}
    />
  )
);
MultiMediaBackground.displayName = "MultiMediaBackground";

export interface MessageBlockProps extends MessageSubComponentProps {
  isTeamMessage?: boolean;
  isRepliedMessage: boolean;
  isFailed?: boolean;
  removeMargin?: boolean;
  removeRadius?: boolean;
  italic?: boolean;
}

export const resolveMessageBackground = ({
  isRepliedMessage,
  isTeamMessage,
  isFailed,
  left,
}: MessageBlockProps): string => {
  if (isFailed) {
    return "var(--inbox-surface-sunken)";
  }
  if (isRepliedMessage) {
    return "var(--accent-blurple-soft)";
  }
  if (left) {
    return "var(--inbox-surface-sunken)";
  }
  return isTeamMessage
    ? "var(--signal-negative-soft)"
    : "var(--accent-blurple-soft)";
};

export const MessageBlock = React.forwardRef<
  HTMLDivElement,
  MessageBlockProps & React.HTMLAttributes<HTMLDivElement>
>(
  (
    {
      left,
      opaque,
      isTeamMessage,
      isRepliedMessage,
      isFailed,
      removeMargin,
      removeRadius,
      italic,
      className,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cn(
        "py-2.5 px-3.5 w-auto max-w-[260px] lg:max-w-[450px] xl:max-w-[550px] break-words whitespace-pre-line text-sm",
        !removeRadius && "rounded-2xl",
        left && !isRepliedMessage && "border border-inbox-border-subtle",
        !removeMargin && "mx-4",
        isRepliedMessage && "-mb-2.5 pb-4 text-xs",
        italic && "italic",
        className
      )}
      {...props}
    />
  )
);
MessageBlock.displayName = "MessageBlock";

export const DateLabel = React.forwardRef<
  HTMLDivElement,
  MessageSubComponentProps & React.HTMLAttributes<HTMLDivElement>
>(({ left, opaque, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-xs text-ink-muted pt-1",
      left ? "pl-[50px]" : "pr-[50px]",
      className
    )}
    {...props}
  />
));
DateLabel.displayName = "DateLabel";

interface MentionLabelProps {
  left?: boolean;
}

export const MentionLabel = React.forwardRef<
  HTMLSpanElement,
  MentionLabelProps & React.HTMLAttributes<HTMLSpanElement>
>(({ left, className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn("italic text-xs mx-4 mb-2.5", !left && "text-end", className)}
    {...props}
  />
));
MentionLabel.displayName = "MentionLabel";

export const BlockContainer = React.forwardRef<
  HTMLDivElement,
  { opaque?: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ opaque, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col", opaque && "opacity-50", className)}
    {...props}
  />
));
BlockContainer.displayName = "BlockContainer";

export const InvalidLabel = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      "text-ink-muted text-xs absolute top-[calc(50%+30px)] -translate-x-1/2 -translate-y-1/2 text-center w-full -ml-[50%]",
      className
    )}
    {...props}
  />
));
InvalidLabel.displayName = "InvalidLabel";

interface MultiMediaContainerProps {
  left?: boolean;
}

export const MultiMediaContainer = React.forwardRef<
  HTMLDivElement,
  MultiMediaContainerProps & React.HTMLAttributes<HTMLDivElement>
>(({ left, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center ml-4 relative [&_video::-webkit-media-controls-panel]:bg-inbox-border",
      !left && "justify-end mr-4",
      className
    )}
    {...props}
  />
));
MultiMediaContainer.displayName = "MultiMediaContainer";

export interface RootContainerProps extends MessageSubComponentProps {
  isFirstInGroup?: boolean;
}

export const RootContainer = React.forwardRef<
  HTMLDivElement,
  RootContainerProps & React.HTMLAttributes<HTMLDivElement>
>(({ left, opaque, isFirstInGroup, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col relative",
      isFirstInGroup === false ? "pt-0.5" : "pt-3",
      !left && "items-end",
      opaque && "opacity-50",
      className
    )}
    {...props}
  />
));
RootContainer.displayName = "RootContainer";

export const RetryContainer = React.forwardRef<
  HTMLDivElement,
  { onLeft?: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ onLeft, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "font-light text-xs leading-[14px] text-signal-negative pr-11 mt-1 [&_.icon]:mr-0.5",
      onLeft ? "text-left pl-11" : "text-right",
      className
    )}
    {...props}
  />
));
RetryContainer.displayName = "RetryContainer";

export const MessageHiddenNote = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn("text-xs italic pt-1 text-center pb-2.5", className)}
    {...props}
  />
));
MessageHiddenNote.displayName = "MessageHiddenNote";

export const ConversationAvatarWrapper = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("self-center", className)} {...props} />
));
ConversationAvatarWrapper.displayName = "ConversationAvatarWrapper";
